import { useState, useEffect, useRef, useMemo } from "react";
import "./chatbot.css";
import Saludando from "./assets/buho-saludando.png";
import Pensando from "./assets/buho-pensando.png";
import Escribiendo from "./assets/buho-escribiendo.png";
import Listo from "./assets/buho-listo.png";
import { obtenerTodosProgramas } from '../../../funtions';

const WHATSAPP_URL = 'https://wa.me/573021233853';

const SPRITES = {
  saludando: Saludando,
  pensando: Pensando,
  escribiendo: Escribiendo,
  listo: Listo,
};

const QUICK_REPLIES = [
  "Quiero ver los programas que tienen",
  "¿Cómo me inscribo?",
  // "Soporte técnico",
  "Certificaciones",
];

const INITIAL_MESSAGES = [
  {
    id: 1,
    role: "bot",
    text: "¡Hola! 👋 Soy el búho de Enseñarte. ¿En qué puedo ayudarte hoy?",
    time: now(),
  },
];

function now() {
  return new Date().toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });
}

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function Chatbot({ open, onClose, estado = "saludando" }) {
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [input, setInput] = useState("");
  const [botState, setBotState] = useState(estado);
  const [isTyping, setIsTyping] = useState(false);
  const [quickVisible, setQuickVisible] = useState(true);
  const [conversationStage, setConversationStage] = useState("idle");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [userName, setUserName] = useState("");
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [programError, setProgramError] = useState(null);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  const categories = useMemo(
    () => Array.from(new Set(programs.map((program) => program.categoria))).sort(),
    [programs]
  );

  useEffect(() => {
    const loadPrograms = async () => {
      try {
        setLoadingPrograms(true);
        const programas = await obtenerTodosProgramas();
        const filteredPrograms = programas
          .filter((p) => p.categoria)
          .map((p) => ({
            id: p.id,
            nombre: p.nombre,
            descripcion: p.descripcion,
            descriptionp: p.descriptionp,
            cardPrincipal: p.cardPrincipal,
            categoria: p.categoria,
          }));
        setPrograms(filteredPrograms);
      } catch (error) {
        console.error('Error cargando programas en chatbot:', error);
        setProgramError(error);
      } finally {
        setLoadingPrograms(false);
      }
    };

    if (open && programs.length === 0 && !loadingPrograms) {
      loadPrograms();
    }
  }, [open, programs.length, loadingPrograms]);

  useEffect(() => {
    if (open && !userName && conversationStage === "idle") {
      const timeout = setTimeout(() => {
        const askNameMessage = {
          id: Date.now(),
          role: "bot",
          text: "Antes de comenzar, ¿cómo te llamas?",
          time: now(),
        };
        setMessages((prev) => [...prev, askNameMessage]);
        setConversationStage("askName");
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [open, userName, conversationStage]);

  const quickReplies = useMemo(() => {
    if (conversationStage === "askName") {
      return [];
    }

    if (conversationStage === "askCategory") {
      return loadingPrograms ? [] : categories;
    }

    if (conversationStage === "categorySelected" && selectedCategory) {
      return programs
        .filter((program) => program.categoria === selectedCategory)
        .map((program) => program.nombre);
    }

    if (conversationStage === "programSelected" || conversationStage === "redirectRegistration") {
      return ["Sí", "No"];
    }

    return QUICK_REPLIES;
  }, [conversationStage, selectedCategory, categories, loadingPrograms, programs]);

  const programsForCategory = (category) =>
    programs.filter(
      (program) => program.categoria.toLowerCase() === category.toLowerCase()
    );

 const initiateRedirect = (url, label, botMessageId) => {
  let count = 3;

  const updateMessage = (text) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === botMessageId
          ? { ...msg, text, time: now() }
          : msg
      )
    );
  };

  updateMessage(`⏳ Redirigiendo a ${label} en ${count}...`);

  const interval = setInterval(() => {
    count--;

    if (count > 0) {
      updateMessage(`⏳ Redirigiendo a ${label} en ${count}...`);
    } else {
      clearInterval(interval);

      updateMessage(`🚀 Abriendo ${label}...`);

      setTimeout(() => {
        window.open(url, "_blank");
      }, 800);
    }
  }, 1000);
};

  const askCategoryReply = () => {
    setConversationStage("askCategory");
    setSelectedCategory("");
    setSelectedProgram(null);
    if (loadingPrograms) {
      return "Un momento, estoy cargando nuestros programas...";
    }
    if (programError) {
      return "No pude cargar los programas ahora mismo. Por favor intenta de nuevo en unos minutos.";
    }
    return `Claro, ¿qué categoría te interesa? Elige una opción:`;
  };

  const handleCategorySelection = (text) => {
    const normalized = text.toLowerCase();
    const matchedCategory = categories.find(
      (category) => category.toLowerCase() === normalized
    );

    if (!matchedCategory) {
      return `No reconocí esa categoría. Prueba con una de estas: ${categories.join(", ")}`;
    }

    const programs = programsForCategory(matchedCategory);
    setSelectedCategory(matchedCategory);
    setConversationStage("categorySelected");
    return `Perfecto, aquí están los programas de ${matchedCategory}. ¿Cuál te ha interesado?`;
  };

  const askRegistrationReply = () => {
    setConversationStage("redirectRegistration");
    setSelectedCategory("");
    setSelectedProgram(null);
    return `La inscripción está en https://web.ensenarteonline.com.co/inscribete. ¿Quieres que te redirija allí?`;
  };

  const buildWhatsAppUrl = () => {
    const programName = selectedProgram?.nombre || "un programa";
    const name = userName || "";
    const text = name
      ? `Hola soy ${name} y estoy interesado en ${programName}`
      : `Hola, estoy interesado en ${programName}`;
    return `${WHATSAPP_URL}?text=${encodeURIComponent(text)}`;
  };

  const handleProgramSelection = (text) => {
    const normalized = text.toLowerCase();
    const programs = programsForCategory(selectedCategory);
    const matchedProgram = programs.find(
      (program) =>
        program.nombre.toLowerCase() === normalized ||
        program.codigo?.toLowerCase() === normalized ||
        program.nombre.toLowerCase().includes(normalized)
    );

    if (!matchedProgram) {
      return `No encontré ese programa en ${selectedCategory}. Elige uno de estos: ${programs
        .map((program) => program.nombre)
        .join(", ")}`;
    }

    setSelectedProgram(matchedProgram);
    setConversationStage("programSelected");
    return `¡Enhorabuena! El programa ${matchedProgram.nombre} es una gran elección. ¿Quieres ser redirigido a un asesor por WhatsApp para recibir más información sobre este programa?`;
  };

  const handleRedirectDecision = (text, url, label, botMessageId) => {
    const normalized = text.toLowerCase();
    const affirmative = normalized.startsWith("s") || normalized.includes("si") || normalized.includes("sí");

    setConversationStage("idle");
    setSelectedCategory("");
    setSelectedProgram(null);

    if (affirmative) {
      initiateRedirect(url, label, botMessageId);
      return `Perfecto, abro ${label} para ti y te redirijo en un momento.`;
    }

    return `Entendido. Si quieres puedo ayudarte a revisar otra categoría o programa.`;
  };

  const handleBotReply = (trimmed, botMessageId) => {
    if (conversationStage === "askCategory") {
      return handleCategorySelection(trimmed);
    }

    if (conversationStage === "categorySelected") {
      return handleProgramSelection(trimmed);
    }

    if (conversationStage === "askName") {
      setUserName(trimmed);
      setConversationStage("idle");
      return `Mucho gusto, ${trimmed}. ¿En qué puedo ayudarte hoy?`; 
    }

    if (conversationStage === "programSelected") {
      return handleRedirectDecision(trimmed, WHATSAPP_URL, "WhatsApp", botMessageId);
    }

    if (conversationStage === "redirectRegistration") {
      return handleRedirectDecision(trimmed, "https://web.ensenarteonline.com.co/inscribete", "la inscripción", botMessageId);
    }

    const lower = trimmed.toLowerCase();
    if (lower.includes("programa") || lower.includes("curso") || lower.includes("cursos")) {
      if (!userName) {
        setConversationStage("askName");
        return "Antes de mostrarte los programas, ¿cómo te llamas?";
      }
      return askCategoryReply();
    }

    if (lower.includes("inscri")) {
      if (!userName) {
        setConversationStage("askName");
        return "Antes de ayudarte con la inscripción, ¿cómo te llamas?";
      }
      return askRegistrationReply();
    }
    // if (lower.includes("soporte") || lower.includes("técnico") || lower.includes("problema")) {
    //   return "Claro, cuéntame qué está pasando y lo resolvemos juntos. 🔧";
    // }
    if (lower.includes("certificac")) {
      return "Al completar cualquier curso recibirás tu certificado digital avalado. 🎓";
    }

    return "Entendido. Dame un momento para buscar la mejor respuesta para ti. 🦉";
  };

  // Scroll al último mensaje
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [quickReplies.length, conversationStage, quickVisible]);

  // Focus en input cuando abre
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 350);
    }
  }, [open]);

  // Sincronizar estado externo del búho
  useEffect(() => {
    if (!isTyping) setBotState(estado);
  }, [estado, isTyping]);

  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || isTyping) return;

    setInput("");
    setQuickVisible(false);

    const userMsg = { id: Date.now(), role: "user", text: trimmed, time: now() };
    setMessages((prev) => [...prev, userMsg]);

    // Pensando
    setBotState("pensando");
    await delay(800);

    // Escribiendo
    setBotState("escribiendo");
    setIsTyping(true);
    await delay(1300);
    setIsTyping(false);

    const botMessageId = Date.now() + 1;
    const reply = handleBotReply(trimmed, botMessageId);
    setMessages((prev) => [
      ...prev,
      { id: botMessageId, role: "bot", text: reply, time: now() },
    ]);

    await delay(1000);
    setBotState("saludando");
    setQuickVisible(true);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!open) return null;

  const owlSprite = SPRITES[botState] || Saludando;

  return (
    <div className="chat-wrap">
      {/* Overlay solo en móvil */}
      <div className="chat-overlay" onClick={onClose} />

      <div className="chat-panel">
        {/* ── Header ── */}
        <div className="chat-header">
          <div className="chat-header-top">
            <div className="chat-header-info">
              <div className="chat-brand">Enseñarte</div>
              <div className="chat-name">Búho Académico</div>
              <div className="chat-status">
                <span className="chat-status-dot" />
                <span className="chat-status-text">En línea · siempre aquí</span>
              </div>
            </div>
            <button className="chat-close" onClick={onClose} aria-label="Cerrar chat">
              ✕
            </button>
          </div>

          {/* Búho flotando sobre el header */}
          <img
            className="chat-header-owl"
            src={owlSprite}
            alt={`búho ${botState}`}
          />
        </div>

        {/* ── Mensajes ── */}
        <div className="chat-body" ref={bodyRef}>
          <div className="chat-date-sep">Hoy</div>

          {messages.map((msg) => (
            <div key={msg.id} className={`chat-msg chat-msg-${msg.role}`}>
              <div className="chat-bubble">{msg.text}</div>
              <span className="chat-time">{msg.time}</span>
            </div>
          ))}

          {isTyping && (
            <div className="chat-typing">
              <img className="chat-typing-owl" src={Escribiendo} alt="escribiendo" />
              <div className="chat-typing-bubble">
                <span /><span /><span />
              </div>
            </div>
          )}
        </div>

        {/* ── Quick replies ── */}
        {quickVisible && quickReplies.length > 0 && (
          <div className="chat-quick">
            {quickReplies.map((q) => (
              <button key={q} className="chat-quick-btn" onClick={() => sendMessage(q)}>
                {q}
              </button>
            ))}
          </div>
        )}

        {/* ── Input ── */}
        <div className="chat-footer">
          <input
            ref={inputRef}
            className="chat-input"
            placeholder="Escribe tu mensaje..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            disabled={isTyping}
          />
          <button
            className="chat-send"
            onClick={() => sendMessage()}
            disabled={isTyping || !input.trim()}
            aria-label="Enviar"
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
}